import React, { useState } from "react";
import PropTypes from "prop-types";
import DropDownButton from "../buttons/DropDownButton";
import SplitButtonCascade from "../buttons/SplitButtonCascade";
import LoadingPanel from "../LoadingPanel";
import Dialog from "../meta/Dialog";
import { buildUrl, getPrimaryField, getFileIcon } from "../utils/helper";
import { addOffset } from "../utils/convert";
import ConfirmDialog from "../dialogs/ConfirmDialog";

const _gridPrintItems = [
  { text: "Tlač mriežky do XLSX", imageUrl: buildUrl("assets/icons/toolbar/ms-excel.svg") },
  { text: "Tlač mriežky do PDF", imageUrl: buildUrl("assets/icons/toolbar/pdf.svg") },
];

/**
 * Print component
 */
const PrintComponent = (props) => {
  const { onTlac, onPrint, isMultiSelected, isSelected, printItems, gridPrintItemsHidden, selected, reducer, componentFactory, tlac, disabled } = props;

  const [isOpenPred, setIsOpenPred] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [state, setState] = useState({
    tl: null,
    isOpenConfirm: false,
    isOpenMultiFiles: false,
    titlePred: "",
    nIdentifPred: "",
    isPrintableToDb: false,
  });

  const handleTlac = async (index, item) => {
    if (item?.iTl > 0) {
      if (item?.iFrmPred) {
        setIsOpenPred(true);
        setState({ ...state, tl: item, titlePred: item?.text, nIdentifPred: item?.nIdentifPred, isPrintableToDb: isPrintableToDb(item?.iSposList) });
      } else if (isMultiSelected && !isSelected && item?.iSposList?.includes(6)) {
        setState({ ...state, isOpenMultiFiles: true, tl: item });
      } else {
        if (isPrintableToDb(item?.iSposList)) {
          setState({ ...state, isOpenConfirm: true, tl: item });
        } else {
          setIsLoading(true);
          await onTlac(item);
          setIsLoading(false);
        }
      }
    } else {
      setIsLoading(true);
      onPrint(index, item.text);
      setIsLoading(false);
    }
  };

  const handleClosePred = () => {
    setIsOpenPred(false);
    setState({ ...state, tl: null, titlePred: "", nIdentifPred: "", isPrintableToDb: false });
  };

  const handleCloseConfirm = () => {
    setState({ ...state, isOpenConfirm: false, tl: null, isPrintableToDb: false });
  };

  const handleCloseMultiFiles = () => {
    setState({ ...state, isOpenMultiFiles: false, tl: null });
  };

  const handleCancelMultiFiles = async () => {
    setIsLoading(true);
    setState({ ...state, isOpenMultiFiles: false });
    const item = { ...state.tl };
    await onTlac(item);
    setState({ ...state, isOpenMultiFiles: false, tl: null });
    setIsLoading(false);
  };

  const handleConfirmMultiFiles = async () => {
    setIsLoading(true);
    setState({ ...state, isOpenMultiFiles: false });
    const item = { ...state.tl, multiFiles: true };
    await onTlac(item);
    setState({ ...state, isOpenMultiFiles: false, tl: null });
    setIsLoading(false);
  };

  const processTlacData = (dialogState) => {
    const detailData = dialogState[dialogState.primaryField]?.detailData;
    let tlac = {
      ...state.tl,
      params: state.tl?.params ?? {},
      dialogState: { ...dialogState },
    };
    for (const [key, value] of Object.entries(detailData)) {
      const field = tlac.dialogState.detailSchema.find((x) => x.field === key);
      const primaryField = getPrimaryField(field?.primaryField);
      if (value?.hasOwnProperty(primaryField)) {
        tlac.params[key + (primaryField === "I_HOD" ? "" : "." + primaryField)] = value[primaryField];
      } else if (value?.hasOwnProperty("start") && value?.hasOwnProperty("end")) {
        tlac.params[key + ".start"] = addOffset(value?.start).toISOString();
        tlac.params[key + ".end"] = addOffset(value?.end).toISOString();
      } else {
        tlac.params[key] = value instanceof Date ? addOffset(value).toISOString() : value;
      }
      if (Array.isArray(value)) {
        let array = value.map((element) => {
          if (element.hasOwnProperty(primaryField)) {
            return element[primaryField];
          } else return element;
        });
        tlac.params[key] = array.join(",");
      }
    }
    return tlac;
  };

  const handleConfirmPred = async (dialogState) => {
    setIsLoading(true);
    const tlac = processTlacData(dialogState);
    setIsOpenPred(false);
    await onTlac(tlac);
    setIsLoading(false);
  };

  const handlePrint = async () => {
    setIsLoading(true);
    handleCloseConfirm();
    setIsOpenPred(false);
    await onTlac(state.tl);
    setIsLoading(false);
  };

  const handleDatabasePrint = async () => {
    setIsLoading(true);
    handleCloseConfirm();
    setIsOpenPred(false);
    await onTlac(state.tl, "database");
    setIsLoading(false);
  };

  const handlePredDatabasePrint = async (state) => {
    setIsLoading(true);
    setState((prevState) => ({ ...prevState, ...state }));
    const tlac = processTlacData(state);
    setIsOpenPred(false);
    await onTlac(tlac, "database");
    setState((prevState) => ({ ...prevState, ...state }));
    setIsLoading(false);
  };

  const handlePreview = async (state) => {
    setIsLoading(true);
    setState((prevState) => ({ ...prevState, ...state }));
    const tlac = processTlacData(state);
    await onTlac(tlac);
    setState((prevState) => ({ ...prevState, ...state }));
    setIsLoading(false);
  };

  const isDisabled = (iSposList) => {
    return (!isMultiSelected && !iSposList?.includes(2) && !iSposList?.includes(4)) || (isSelected && !iSposList?.includes(1) && !iSposList?.includes(3) && !iSposList?.includes(4)) || (isMultiSelected && !isSelected && !iSposList?.includes(3) && !iSposList?.includes(4) && !iSposList?.includes(6));
  };

  const isPrintableToDb = (iSposList) => {
    return iSposList?.includes(5);
  };

  const getTlacKatItem = (tlacKat) => {
    return {
      text: tlacKat.text,
      disableClick: true,
      render: SplitButtonCascade,
      direction: "left",
      onItemClick: (index, item) => handleTlac(index, item),
      items: tlacKat.items.map((x) => ({
        ...x,
        disabled: x.disabled || isDisabled(x.iSposList),
        imageUrl: buildUrl(getFileIcon(x.extension))
      })),
    };
  };

  const getTlacItem = (tlac) => {
    return {
      ...tlac,
      disabled: tlac.disabled || isDisabled(tlac.iSposList),
      imageUrl: buildUrl(getFileIcon(tlac.extension)),
      onItemClick: (index, item) => handleTlac(index, item),
    };
  };

  const getPrintItems = () => {
    if (printItems?.length > 0) {
      return [..._gridPrintItems, ...printItems];
    }
    let newPrintItems = gridPrintItemsHidden
      ? []
      : [
        {
          text: "Tlač mriežky",
          disableClick: true,
          render: SplitButtonCascade,
          direction: "left",
          onItemClick: (index, item) => handleTlac(index, item),
          items: _gridPrintItems,
        },
      ];
    if (tlac?.tlacKatList?.length > 0) {
      newPrintItems = [...newPrintItems, ...tlac.tlacKatList.map(getTlacKatItem)];
    }
    if (tlac?.items?.length > 0) {
      newPrintItems = [...newPrintItems, ...tlac.items.map(getTlacItem)];
    }
    return newPrintItems;
  };

  const handlePrintReducer = async (action, data, state, prevState) => {
    state.selected = selected;
    return await reducer(action, data, state, prevState);
  };

  return (
    <>
      <DropDownButton popupSettings={{ popupClass: "dropdown-print" }} title="Tlač" items={getPrintItems()} onClick={onPrint} disabled={disabled} imageUrl={buildUrl("assets/icons/toolbar/print.svg")} />
      {isOpenPred &&
        (state.isPrintableToDb ? (
          <Dialog
            primaryField={state.nIdentifPred}
            className={state.nIdentifPred}
            isOpen={isOpenPred}
            onClose={handleClosePred}
            onConfirm={handlePredDatabasePrint}
            onMiddleClick={handlePreview}
            middleText="Náhľad"
            title={state.titlePred}
            reducer={handlePrintReducer}
            componentFactory={componentFactory}
            confirmText="Uložiť do databázy"
          />
        ) : (
          <Dialog primaryField={state.nIdentifPred} className={state.nIdentifPred} isOpen={isOpenPred} onClose={handleClosePred} onConfirm={handleConfirmPred} title={state.titlePred} reducer={handlePrintReducer} componentFactory={componentFactory} confirmText="Vytlačiť" />
        ))}
      {state.isOpenConfirm && <ConfirmDialog text={`Vyberte požadovanú formu tlače pre tlažovú zostavu: ${state.tl.text}`} onClose={handleCloseConfirm} onCancel={handleDatabasePrint} onConfirm={handlePrint} isOpen={state.isOpenConfirm} confirmText="Vytlačiť" cancelText="Uložiť do databázy" />}
      {state.isOpenMultiFiles && <ConfirmDialog text={"Je označených viacero položiek. Chcete tlačovú zostavu rozdeliť do viacerých súborov?"} onClose={handleCloseMultiFiles} onCancel={handleCancelMultiFiles} onConfirm={handleConfirmMultiFiles} isOpen={state.isOpenMultiFiles} confirmText="Áno, rozdeliť do viacerých súborov" cancelText="Nie, spojiť do jedného súboru" />}
      <LoadingPanel isLoading={isLoading} />
    </>
  );
};

PrintComponent.propTypes = {
  disabled: PropTypes.bool,
  printItems: PropTypes.array,
  onPrint: PropTypes.func,
  tlac: PropTypes.object,
  onTlac: PropTypes.func,
  isSelected: PropTypes.bool,
  isMultiSelected: PropTypes.bool,
  reducer: PropTypes.func,
  componentFactory: PropTypes.func,
  gridPrintItemsHidden: PropTypes.bool,
};

export default PrintComponent;
